// Lightweight document <head> manager so each route/locale combination gets
// correct <title>, meta description, canonical and hreflang tags at runtime.
// Kept dependency-free (no vue-meta) since this is a small, fixed set of routes.
import { LOCALES, DEFAULT_LOCALE, localeInfo, localePath } from './locales';

const BASE_URL = 'https://remote-emdr.nl';

function upsertLink(rel, hreflang, href) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeLink(rel, hreflang) {
  const el = document.head.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
  if (el) el.remove();
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Applies SEO-relevant tags for the current page.
 * @param {Object} opts
 * @param {string} opts.locale - a code from content/i18n/locales.json, e.g. 'en' | 'pl'
 * @param {string} opts.routeBase - '' | 'patient' | 'controller' (locale-agnostic path)
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {boolean} [opts.noindex] - true for session tool pages that shouldn't be indexed
 * @param {string[]} [opts.languages] - languages the page exists in (default: all)
 */
export function applySeo({ locale, routeBase, title, description, noindex, languages = LOCALES }) {
  if (typeof document === 'undefined') return;

  const suffix = routeBase ? `/${routeBase}` : '/';
  const urlFor = code => `${BASE_URL}${localePath(suffix, code)}`;
  const currentUrl = urlFor(locale);

  document.documentElement.lang = locale;
  document.title = title;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

  upsertLink('canonical', null, currentUrl);
  LOCALES.forEach(code => {
    if (languages.indexOf(code) !== -1) upsertLink('alternate', localeInfo(code).hreflang, urlFor(code));
    else removeLink('alternate', localeInfo(code).hreflang);
  });
  upsertLink('alternate', 'x-default', urlFor(DEFAULT_LOCALE));

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', currentUrl);
  upsertMeta('property', 'og:locale', localeInfo(locale).ogLocale);
  upsertMeta('property', 'og:image', `${BASE_URL}/og-image-${locale}.jpg`);
  upsertMeta('name', 'twitter:image', `${BASE_URL}/og-image-${locale}.jpg`);
}
